import React, { useMemo, useState, useCallback, useContext } from 'react';
import { createPortal } from 'react-dom';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}

var DialogContext = React.createContext({
    instances: [],
    events: [],
    setEvents: function () {
        throw new Error('setEvents not implement');
    },
    setInstances: function () {
        throw new Error('setInstance not implement');
    },
    config: {},
});

var actions = {
    close: 'close',
    success: 'success',
    cancel: 'cancel',
};

/**
 * Provider create context for `useDialog` hook.
 *
 * **You can use it once in app.**
 *
 * @example
 * import { Provider as ModalProvider } from 'react-modaly';
 * import Layout from './modals/Layout';
 *
 * const MODAL_NODE = document.getElementById('modal');
 *
 * const config = {
 *   'account/information': () => React.lazy(() => import('./modals/account/Information.js'));
 * };
 *
 * const App = ({ children }) => (
 *   <ModalProvider node={MODAL_NODE} config={config} Layout={Layout}>
 *     {children}
 *   </ModalProvider>
 * );
 */
var Provider = function (_a) {
    var children = _a.children, node = _a.node, config = _a.config, Layout = _a.Layout;
    var _b = useState([]), instances = _b[0], setInstances = _b[1];
    var _c = useState([]), events = _c[0], setEvents = _c[1];
    var close = useCallback(function () {
        var resolve = events[events.length - 1].resolve;
        var resolveParams = { action: actions.close };
        setInstances(function (prevInstances) { return prevInstances.filter(function (_, index) { return index !== prevInstances.length - 1; }); });
        setEvents(function (prevEvents) { return prevEvents.filter(function (_, index) { return index !== prevEvents.length - 1; }); });
        resolve(resolveParams);
    }, [events]);
    var cancel = useCallback(function (values) {
        var resolve = events[events.length - 1].resolve;
        var resolveParams = { action: actions.cancel, values: values };
        setInstances(function (prevInstances) { return prevInstances.filter(function (_el, index) { return index !== prevInstances.length - 1; }); });
        setEvents(function (prevEvents) { return prevEvents.filter(function (_el, index) { return index !== prevEvents.length - 1; }); });
        resolve(resolveParams);
    }, [events]);
    var success = useCallback(function (values) {
        var resolve = events[events.length - 1].resolve;
        var resolveParams = { action: actions.success, values: values };
        setInstances(function (prevInstances) { return prevInstances.filter(function (_el, index) { return index !== prevInstances.length - 1; }); });
        setEvents(function (prevEvents) { return prevEvents.filter(function (_el, index) { return index !== prevEvents.length - 1; }); });
        resolve(resolveParams);
    }, [events]);
    var context = {
        instances: instances,
        setInstances: setInstances,
        events: events,
        setEvents: setEvents,
        config: config,
    };
    var Component = instances.map(function (instance) { return (React.createElement(Layout, __assign({ key: instance.instanceName, component: config[instance.instanceName], cancel: cancel, success: success, close: close }, instance))); });
    var child = useMemo(function () { return React.Children.only(children); }, [children]);
    return (React.createElement(DialogContext.Provider, { value: context },
        React.createElement(React.Fragment, null,
            child,
            createPortal(Component, node))));
};

/**
 * useDialog is hook which can be used to open modal window.
 * useDialog return `open` function which is Promise.
 * This promise will resolved/rejected when modal window will closed.
 *
 * @example
 * import { useState } from 'react';
 * import { actions } from 'react-modaly';
 * const Button = () => {
 *  const [disabled, setDisabled] = useState(false);
 *
 *  const { open } = useDialog();
 *  const onClick = () => {
 *    open('account/information', { isShowIcon: true })
 *      .then(({ values }) => setDisabled(values.rightAccess));
 *  };
 *
 *
 *  return (
 *    <button onClick={onClick} disabled={disabled}>Choose something into modal</button>
 *  );
 * };
 */
var useDialog = function () {
    var _a = useContext(DialogContext), setEvents = _a.setEvents, setInstances = _a.setInstances, config = _a.config;
    var open = function (instance) {
        return new Promise(function (resolve, reject) {
            if (instance.instanceName in config) {
                setInstances(function (prevInstances) { return __spreadArrays(prevInstances, [instance]); });
                setEvents(function (prevEvents) { return __spreadArrays(prevEvents, [{ resolve: resolve, reject: reject }]); });
            }
            else {
                throw new Error(instance['instanceName'] + " don't exist in modal config");
            }
        });
    };
    return { open: open };
};

export { Provider, useDialog, actions };
//# sourceMappingURL=index.es.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/react-modaly/dist/index.es.js
// module id = 39
// module chunks = 1